import React, { useMemo, useState } from 'react'
import CampaignSearchInput from '../CampaignSearchInput'
import FilterDropdown from '../FilterDropdown'
import CampaignsPagination from '../CampaignsPagination'

function matchesSearch(row, search, searchKeys) {
  if (!search) return true
  const haystack = (searchKeys?.length ? searchKeys : Object.keys(row))
    .map((key) => String(row[key] ?? ''))
    .join(' ')
    .toLowerCase()
  return haystack.includes(search.toLowerCase())
}

export default function TabTable({
  title, columns, rows = [], searchPlaceholder = 'Search', searchKeys, statusOptions, statusKey = 'status',
  secondaryFilter, onRowClick, pageSizeDefault = 10
}) {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(pageSizeDefault)

  const filtered = useMemo(() => rows.filter((row) => {
    if (!matchesSearch(row, search, searchKeys)) return false
    if (status && String(row[statusKey] ?? '').toLowerCase() !== status.toLowerCase()) return false
    return true
  }), [rows, search, status, searchKeys, statusKey])

  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize)

  return (
    <div className="w-full rounded-2xl border border-border-lines">
      {title && <p className="text-base font-semibold text-dark-blue px-4 pt-4">{title}</p>}

      <div className="flex flex-wrap items-center gap-3 p-4">
        <CampaignSearchInput value={search} onChange={(value) => { setSearch(value); setPage(1) }} placeholder={searchPlaceholder} />

        {
          statusOptions?.length > 0 && (
            <FilterDropdown
              label="All statuses"
              active={status ? statusOptions.find((option) => option.value === status)?.label : null}
              panel={({ close }) => (
                <ul className="flex flex-col">
                  {
                    statusOptions.map((option) => (
                      <li key={option.value || 'all'}>
                        <button
                          type="button"
                          onClick={() => { setStatus(option.value); setPage(1); close() }}
                          className="w-full text-left px-2 py-2 text-sm text-dark-blue hover:bg-background rounded-lg"
                        >
                          {option.label}
                        </button>
                      </li>
                    ))
                  }
                </ul>
              )}
            />
          )
        }

        {secondaryFilter}
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="bg-background">
              {columns.map((column) => (
                <th key={column.key} className="text-left font-semibold text-dark-blue px-4 py-2.5 whitespace-nowrap">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {
              paginated.map((row, index) => (
                <tr
                  key={row.id ?? index}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-border-lines last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-background' : ''}`}
                >
                  {
                    columns.map((column) => (
                      <td key={column.key} className="px-4 py-3 text-dark-blue whitespace-nowrap">
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))
                  }
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <CampaignsPagination
        total={filtered.length}
        currentPage={page}
        lastPage={lastPage}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
        selectedCount={0}
      />
    </div>
  )
}
